import { Autocomplete, Box, Divider, IconButton, InputAdornment, ListSubheader, MenuItem, Popover, Stack, TextField, Tooltip, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { useEffect, useMemo, useState } from "react";
import { useColumns } from "app/hooks/useColumns";
import { useTables } from "app/hooks/useTables";
import SysColumn from "app/class/sys-column";
import Translate from "component/global/translation/translation-component";
import { useTranslation } from "react-i18next";
import { translateMetadataLabel } from "modules/record/shared/services/recordMetadataLabels";
import ReferencePathFieldSelector from "./ReferencePathFieldSelector";
import { columnsForTable } from "./referencePath";
import { useReferenceDotWalkMaxDepth } from "./useReferenceDotWalkMaxDepth";
import { isCompatibleColumnType } from "./columnTypeCompatibility";
import { SYSTEM_DYNAMIC_OPTIONS } from "./systemDynamicOptions";

interface DynamicValueFieldProps {
    value: any;
    columns: SysColumn[];
    selectedFieldType?: string; // Tipo do campo selecionado para filtrar opções compatíveis
    allColumns?: SysColumn[];
    tables?: any[];
    maxReferenceDepth?: number;
    formFields?: { name: string; label: string }[];
    onChange: (value: any) => void;
}

const EXTERNAL_TABLE_FIELD_OPTION = "__external_table_field__";

export const DynamicValueField = ({
    value,
    columns,
    selectedFieldType,
    allColumns,
    tables: providedTables,
    maxReferenceDepth,
    formFields = [],
    onChange
}: DynamicValueFieldProps) => {
    const { t } = useTranslation();
    const { data: queriedTables = [] } = useTables({ enabled: !providedTables });
    const { data: queriedAllColumns = [] } = useColumns({ enabled: !allColumns });
    const maxDepthQuery = useReferenceDotWalkMaxDepth();
    const tables = providedTables || queriedTables;
    const resolvedAllColumns = allColumns || queriedAllColumns;
    const resolvedMaxReferenceDepth = maxReferenceDepth || maxDepthQuery.maxDepth;
    const sortedTables = useMemo(
        () => [...tables]
            .filter((table) => table.id && table.name)
            .sort((left, right) =>
                translateMetadataLabel(t, left).localeCompare(
                    translateMetadataLabel(t, right),
                    undefined,
                    { sensitivity: "base" }
                )
            ),
        [t, tables]
    );

    const compatibleColumns = useMemo(
        () => columns.filter((column) => isCompatibleColumnType(column, selectedFieldType)),
        [columns, selectedFieldType]
    );

    const compatibleSystemOptions = useMemo(
        () => (
            selectedFieldType
                ? SYSTEM_DYNAMIC_OPTIONS.filter((opt) => opt.compatibleTypes.includes(selectedFieldType))
                : SYSTEM_DYNAMIC_OPTIONS
        ),
        [selectedFieldType]
    );

    const resolvedValue = typeof value === "string" ? value : "";
    const dynamicPath = resolvedValue.match(/^\$\{([^}]+)\}$/)?.[1] ?? "";
    const dynamicSegments = dynamicPath ? dynamicPath.split(".") : [];
    const externalTableName = dynamicSegments.length >= 2 ? dynamicSegments[0] : "";
    const externalFieldName = dynamicSegments.length >= 2 ? dynamicSegments.slice(1).join(".") : "";

    const externalTable = useMemo(
        () => tables.find((table) => table.name === externalTableName) ?? null,
        [externalTableName, tables]
    );

    const [isExternalSelectionActive, setIsExternalSelectionActive] = useState(Boolean(externalTableName));
    const [selectedExternalTableId, setSelectedExternalTableId] = useState(externalTable?.id ?? "");
    const [selectedExternalFieldName, setSelectedExternalFieldName] = useState(externalFieldName);
    const [fieldPickerAnchorEl, setFieldPickerAnchorEl] = useState<HTMLElement | null>(null);

    useEffect(() => {
        setIsExternalSelectionActive(Boolean(externalTableName));
        setSelectedExternalTableId(externalTable?.id ?? "");
        setSelectedExternalFieldName(externalFieldName);
    }, [externalFieldName, externalTable?.id, externalTableName]);

    const externalTableColumns = useMemo(
        () => columnsForTable(resolvedAllColumns, selectedExternalTableId),
        [resolvedAllColumns, selectedExternalTableId]
    );

    const pickerValue = useMemo(() => {
        if (compatibleSystemOptions.some((option) => option.value === resolvedValue)) {
            return resolvedValue;
        }

        if (compatibleColumns.some((column) => `\$\{${column.name}\}` === resolvedValue)) {
            return resolvedValue;
        }

        if (formFields.some((field) => `$form.${field.name}` === resolvedValue)) {
            return resolvedValue;
        }

        if (externalTableName && externalFieldName) {
            return EXTERNAL_TABLE_FIELD_OPTION;
        }

        return "";
    }, [compatibleColumns, compatibleSystemOptions, externalFieldName, externalTableName, formFields, resolvedValue]);

    const displayedPickerValue = isExternalSelectionActive
        ? EXTERNAL_TABLE_FIELD_OPTION
        : pickerValue;

    const externalDisplayValue = [
        sortedTables.find((table) => table.id === selectedExternalTableId)?.name || externalTableName,
        selectedExternalFieldName
    ].filter(Boolean).join(".");
    const fieldPickerOpen = Boolean(fieldPickerAnchorEl);
    const selectedExternalTable = useMemo(
        () => sortedTables.find((table) => table.id === selectedExternalTableId) ?? null,
        [selectedExternalTableId, sortedTables]
    );

    const handleExternalFieldChange = (fieldName: string) => {
        const tableName = tables.find((table) => table.id === selectedExternalTableId)?.name;

        setIsExternalSelectionActive(true);
        setSelectedExternalFieldName(fieldName);

        if (!tableName || !fieldName) {
            return;
        }

        onChange(`\$\{${tableName}.${fieldName}\}`);
    };

    const clearExternalSelection = () => {
        setIsExternalSelectionActive(false);
        setSelectedExternalTableId("");
        setSelectedExternalFieldName("");
        setFieldPickerAnchorEl(null);
        onChange("");
    };

    return (
        <Stack spacing={1}>
            {isExternalSelectionActive ? (
                <TextField
                    size="small"
                    value={externalDisplayValue || t("Select table and field")}
                    onClick={(event) => setFieldPickerAnchorEl(event.currentTarget)}
                    inputProps={{ readOnly: true, "aria-label": String(t("Condition value")) }}
                    sx={{
                        minWidth: 220,
                        width: "100%",
                        cursor: "pointer",
                        "& .MuiInputBase-input": {
                            cursor: "pointer",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        },
                    }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <Tooltip title={t("Edit field selection")}>
                                    <IconButton
                                        aria-label={String(t("Edit field selection"))}
                                        size="small"
                                        edge="end"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            setFieldPickerAnchorEl(event.currentTarget);
                                        }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={t("Clear selection")}>
                                    <IconButton
                                        aria-label={String(t("Clear selection"))}
                                        size="small"
                                        edge="end"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            clearExternalSelection();
                                        }}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </InputAdornment>
                        ),
                    }}
                />
            ) : (
                <TextField
                    select
                    size="small"
                    inputProps={{ "aria-label": String(t("Condition value")) }}
                    value={displayedPickerValue}
                    onChange={(e) => {
                        const nextValue = e.target.value;

                        if (nextValue === EXTERNAL_TABLE_FIELD_OPTION) {
                            setIsExternalSelectionActive(true);
                            setFieldPickerAnchorEl(e.currentTarget);
                            return;
                        }

                        setIsExternalSelectionActive(false);
                        setSelectedExternalTableId("");
                        setSelectedExternalFieldName("");
                        onChange(nextValue);
                    }}
                    sx={{ minWidth: 200, width: "100%" }}
                >
                    <MenuItem value="">
                        <em><Translate text="Select dynamic value" /></em>
                    </MenuItem>

                    {compatibleSystemOptions.length > 0 && (
                        <ListSubheader><Translate text="System Values" /></ListSubheader>
                    )}
                    {compatibleSystemOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                            <Translate text={opt.label} />
                        </MenuItem>
                    ))}

                    {compatibleSystemOptions.length > 0 && compatibleColumns.length > 0 && <Divider />}

                    {compatibleColumns.length > 0 && (
                        <ListSubheader><Translate text="Current Table Fields" /></ListSubheader>
                    )}
                    {compatibleColumns.map((col) => (
                        <MenuItem key={col.name} value={`$\{${col.name}}`}>
                            {translateMetadataLabel(t, col)}
                        </MenuItem>
                    ))}

                    {(compatibleSystemOptions.length > 0 || compatibleColumns.length > 0) && formFields.length > 0 && <Divider />}

                    {formFields.length > 0 && (
                        <ListSubheader><Translate text="Current Form Fields" /></ListSubheader>
                    )}
                    {formFields.map((field) => (
                        <MenuItem key={`form.${field.name}`} value={`$form.${field.name}`}>
                            {field.label || field.name}
                        </MenuItem>
                    ))}

                    <Divider />
                    <ListSubheader><Translate text="Cross Table Field" /></ListSubheader>
                    <MenuItem value={EXTERNAL_TABLE_FIELD_OPTION}>
                        <Translate text="Select table and field" />
                    </MenuItem>
                </TextField>
            )}

            <Popover
                open={fieldPickerOpen}
                anchorEl={fieldPickerAnchorEl}
                onClose={() => setFieldPickerAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{
                    sx: {
                        width: { xs: "calc(100vw - 32px)", sm: 520 },
                        maxWidth: "calc(100vw - 32px)",
                        p: 1.5,
                    },
                }}
            >
                <Stack spacing={1.25}>
                    <Autocomplete
                        size="small"
                        options={sortedTables}
                        value={selectedExternalTable}
                        onChange={(_, table) => {
                            setIsExternalSelectionActive(true);
                            setSelectedExternalTableId(table?.id || "");
                            setSelectedExternalFieldName("");
                        }}
                        getOptionLabel={(table) => table.name
                            ? `${translateMetadataLabel(t, table)} (${table.name})`
                            : ""
                        }
                        isOptionEqualToValue={(option, currentValue) => option.id === currentValue.id}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t("Table")}
                                placeholder={String(t("Search table"))}
                            />
                        )}
                        sx={{ width: "100%" }}
                    />

                    <Box sx={{ width: "100%", minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.25 }}>
                            {t("Field")}
                        </Typography>
                        {selectedExternalTableId ? (
                            <ReferencePathFieldSelector
                                value={selectedExternalFieldName}
                                columns={externalTableColumns}
                                allColumns={resolvedAllColumns}
                                tables={tables}
                                maxDepth={resolvedMaxReferenceDepth}
                                isColumnSelectable={(column) => isCompatibleColumnType(column, selectedFieldType)}
                                onChange={handleExternalFieldChange}
                            />
                        ) : (
                            <TextField size="small" value="" disabled sx={{ width: "100%" }} />
                        )}
                    </Box>
                </Stack>
            </Popover>
        </Stack>
    );
};
